import { useMemo } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import { TYPE } from '@/theme/typography';

// ── Illustration registry ──
// Add new illustrations here — just drop SVGs into public/illustrations/ and register them.

interface IllustrationEntry {
  src: string;
  alt: string;
  tags: string[]; // pages/contexts where this illustration fits
}

const ILLUSTRATIONS: IllustrationEntry[] = [
  {
    src: '/illustrations/social-dashboard.svg',
    alt: 'Social dashboard with people and media icons',
    tags: ['portal', 'dashboard', 'welcome', 'general'],
  },
  // Add more as you download them from undraw.co:
  // { src: '/illustrations/team-work.svg', alt: '...', tags: ['hr', 'portal'] },
  // { src: '/illustrations/finance.svg', alt: '...', tags: ['accounting', 'budget'] },
];

interface PageIllustrationProps {
  /** Page context to match illustrations (e.g. 'portal', 'hr', 'accounting') */
  context?: string;
  /** Specific illustration path — overrides random selection */
  src?: string;
  /** Optional greeting text displayed alongside the illustration */
  greeting?: string;
  /** Optional subtitle text */
  subtitle?: string;
  /** Max height of the illustration */
  maxHeight?: number;
  /** Layout variant */
  variant?: 'hero' | 'inline' | 'empty-state';
}

/**
 * Displays a decorative undraw illustration.
 *
 * Usage:
 *   <PageIllustration context="portal" greeting="Welcome back, Ola" />
 *   <PageIllustration src="/illustrations/team-work.svg" variant="empty-state" />
 *   <PageIllustration context="hr" variant="inline" maxHeight={120} />
 */
export default function PageIllustration({
  context,
  src,
  greeting,
  subtitle,
  maxHeight = 180,
  variant = 'hero',
}: PageIllustrationProps) {
  const theme = useTheme();

  // Pick an illustration — specific src wins, otherwise match by context/tag, fallback to random
  const illustration = useMemo(() => {
    if (src) return { src, alt: 'Page illustration', tags: [] };

    const matched = context
      ? ILLUSTRATIONS.filter((i) => i.tags.includes(context))
      : ILLUSTRATIONS;

    const pool = matched.length > 0 ? matched : ILLUSTRATIONS;
    if (pool.length === 0) return null;

    // Deterministic-ish daily rotation so it doesn't flicker on re-render
    const dayIndex = Math.floor(Date.now() / 86400000) % pool.length;
    return pool[dayIndex];
  }, [context, src]);

  if (!illustration) return null;

  if (variant === 'empty-state') {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Box
          component="img"
          src={illustration.src}
          alt={illustration.alt}
          sx={{
            maxHeight,
            maxWidth: '100%',
            opacity: 0.85,
            mb: 2,
            animation: 'fadeInUp 0.6s ease-out',
            '@keyframes fadeInUp': {
              from: { opacity: 0, transform: 'translateY(12px)' },
              to: { opacity: 0.85, transform: 'translateY(0)' },
            },
          }}
        />
        {greeting && (
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: TYPE.medium }}>
            {greeting}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    );
  }

  if (variant === 'inline') {
    return (
      <Box
        component="img"
        src={illustration.src}
        alt={illustration.alt}
        sx={{
          maxHeight,
          maxWidth: '100%',
          display: 'block',
          animation: 'fadeIn 0.5s ease-out',
          '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      />
    );
  }

  // Hero variant — banner with gradient background
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3,
        mb: 2.5,
        p: { xs: 1.5, sm: 2 },
        borderRadius: '14px',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.1),
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Text side */}
      <Box sx={{ flex: 1, minWidth: 0, zIndex: 1 }}>
        {greeting && (
          <Typography
            variant="h5"
            sx={{
              fontWeight: TYPE.bold,
              color: 'text.primary',
              mb: subtitle ? 0.5 : 0,
            }}
          >
            {greeting}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Illustration side */}
      <Box
        component="img"
        src={illustration.src}
        alt={illustration.alt}
        sx={{
          maxHeight,
          maxWidth: { xs: '40%', sm: '35%', md: '30%' },
          flexShrink: 0,
          display: { xs: 'none', sm: 'block' },
          animation: 'fadeInRight 0.6s ease-out',
          '@keyframes fadeInRight': {
            from: { opacity: 0, transform: 'translateX(20px)' },
            to: { opacity: 1, transform: 'translateX(0)' },
          },
        }}
      />
    </Box>
  );
}
